'use client';

import { useEffect, useLayoutEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useLocale } from 'next-intl';
import { AuthHandoffSurface } from './auth-handoff-surface';
import { getAuthPageCopy } from '@/i18n/auth-page-copy';
import {
  AUTH_HANDOFF_MAX_MS,
  AUTH_HANDOFF_PENDING_MAX_MS,
  isSigningInPath,
  resolveAuthHandoffCover,
} from '@/lib/auth/auth-handoff-phase';
import {
  endAuthHandoff,
  markAuthHandoffAtSigningIn,
  readAuthHandoffPendingHref,
  readAuthHandoffState,
  useAuthHandoffState,
  yieldAuthHandoff,
} from '@/lib/auth/auth-handoff';

/** Fallback for a reveal whose transitionend never fires (hidden tab). */
const REVEAL_MAX_MS = 900;

/**
 * The bridge between the landing-style auth pages and the app (#5559): one
 * app-theme surface that covers every sign-in path from the moment it
 * succeeds until the destination has painted, then dissolves into it.
 *
 * Mounted once in the root layout, so soft navigations never remount it and
 * the route loaders in between stay underneath (no second loader, no blink).
 * It renders on the server for `/auth/signing-in`, where the Google return
 * and Electron's post-handoff document load land. No visible copy: the
 * spinner sits where Electron's splash and `FullScreenLoader` put theirs, so
 * nothing shifts and no text can show in the wrong language.
 */
export function AuthHandoffVeil() {
  const pathname = usePathname();
  const handoff = useAuthHandoffState();
  const cover = resolveAuthHandoffCover(pathname, handoff);
  const onSigningIn = isSigningInPath(pathname);
  const copy = getAuthPageCopy(useLocale());
  // A first-paint veil is opaque at once; a veil a sign-in starts fades in.
  const [entrance, setEntrance] = useState<'none' | 'fade'>(() =>
    onSigningIn ? 'none' : 'fade'
  );
  const [revealing, setRevealing] = useState(false);
  const finishReveal = () => {
    setRevealing(false);
    setEntrance('fade');
  };

  // Before paint: a stale yield from an earlier handoff must not show.
  useLayoutEffect(() => {
    if (onSigningIn) markAuthHandoffAtSigningIn();
  }, [onSigningIn]);

  // Lift once the destination has painted two frames with nothing loading.
  useEffect(() => {
    if (cover !== 'release') return;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => {
        if (
          resolveAuthHandoffCover(pathname, readAuthHandoffState()) !==
          'release'
        )
          return;
        setRevealing(true);
        endAuthHandoff();
      });
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [cover, pathname]);

  // A soft sign-in whose navigation never lands loads /auth/signing-in as a
  // document instead (the session is already set); without a target, the
  // form comes back.
  const pendingNavigation =
    handoff.active && !handoff.reachedSigningIn && !onSigningIn;
  useEffect(() => {
    if (!pendingNavigation) return;
    const timer = window.setTimeout(() => {
      const href = readAuthHandoffPendingHref();
      if (href) window.location.assign(href);
      else yieldAuthHandoff();
    }, AUTH_HANDOFF_PENDING_MAX_MS);
    return () => window.clearTimeout(timer);
  }, [pendingNavigation]);

  // One deadline per handoff, on every route: nothing covers for good.
  const covering = handoff.active && !handoff.yielded;
  const generation = handoff.generation;
  useEffect(() => {
    if (!covering) return;
    const timer = window.setTimeout(yieldAuthHandoff, AUTH_HANDOFF_MAX_MS);
    return () => window.clearTimeout(timer);
  }, [covering, generation]);

  useEffect(() => {
    if (!revealing || cover !== 'idle') return;
    const timer = window.setTimeout(() => {
      setRevealing(false);
      setEntrance('fade');
    }, REVEAL_MAX_MS);
    return () => window.clearTimeout(timer);
  }, [revealing, cover]);

  const state =
    cover !== 'idle' ? 'covering' : revealing ? 'revealing' : 'hidden';
  if (state === 'hidden') return null;
  return (
    <AuthHandoffSurface
      state={state}
      entrance={entrance}
      label={copy.loggingYouIn}
      onTransitionEnd={(event) => {
        if (
          state === 'revealing' &&
          event.target === event.currentTarget &&
          event.propertyName === 'opacity'
        )
          finishReveal();
      }}
    />
  );
}
